import Phaser from 'phaser';
import SoundManager from '../audio/SoundManager';
import { setGameContext, getGameContext } from '../utils/context';
import { preloadAllSounds } from '../audio/loadSounds';
import { BGM, SFX } from '../audio/AudioKeys';
import { initSceneContext } from '../utils/initContext';

/**
 * Changes:
 * - Preload Kenney "Man Blue" directional frames (stand/hold/gun/machine/silencer) as an atlas using existing XML.
 * - Generate Phaser animations for movement in four directions and idle frames.
 *   We simulate direction by rotating the same texture since Kenney pack is top-down aiming forward.
 */
export class BootScene extends Phaser.Scene {
    constructor() {
        super({ key: 'BootScene' });
    }

    preload() {
        // Load assets by static URLs from /public so Phaser receives string URLs (not imported objects)
        this.load.tilemapTiledJSON('level1', '/assets/tilemaps/level1.json');
        this.load.image('tiles', '/assets/kenney_top-down-shooter/Tilesheet/tilesheet_complete.png');

        // Player base texture (kept for compatibility) - not used for animations directly
        this.load.image('player', '/assets/kenney_top-down-shooter/PNG/Man Blue/manBlue_stand.png');

        // Zombies (unchanged)
        this.load.image('zombie', '/assets/kenney_top-down-shooter/PNG/Hitman 1/hitman1_gun.png');

        // Load Kenney characters atlas that includes manBlue_* frames
        // The XML references an imagePath "sprites.png"; the provided sheet is spritesheet_characters.png,
        // Phaser ignores XML imagePath and uses the supplied texture key.
        this.load.atlasXML(
            'kenneyChars',
            '/assets/kenney_top-down-shooter/Spritesheet/spritesheet_characters.png',
            '/assets/kenney_top-down-shooter/Spritesheet/spritesheet_characters.xml'
        );
        // Power-up: pistol icon
        this.load.image('pistolPowerup', '/assets/kenney_top-down-shooter/PNG/weapon_gun.png');
        // Projectile sprite for bullets (Task 5.3)
        this.load.image('bullet', '/assets/kenney_top-down-shooter/PNG/Tiles/tile_470.png');

        // Audio: centralized map. Uses '/audio/...' URLs under public/.
        preloadAllSounds(this.load);
    }

    create() {
        // Ensure GameContext and SoundManager are initialized for this scene
        initSceneContext(this);

        // Minimal example: play a UI click once assets are available (commented out to avoid auto-play)
        // if (this.sound.cache.audio.has(SFX.UiClick)) {
        //   this.sound.play(SFX.UiClick);
        // }

        // Build simple "Man Blue" animation sets.
        // We will reuse a single "side" walk loop for all directions and control flip/angle in Player.
        // Existing four-direction keys are kept for compatibility.

        const stand = 'manBlue_stand.png';
        const hold = 'manBlue_hold.png';
        const gun = 'manBlue_gun.png';
        const machine = 'manBlue_machine.png';
        const silencer = 'manBlue_silencer.png';

        // Base walk/idle used for all directions
        const walkFrames = [hold, gun, machine, silencer];

        // Side/base animations (canonical keys Player will use)
        this.anims.create({
            key: 'player-idle_side',
            frames: [{ key: 'kenneyChars', frame: stand }],
            frameRate: 1,
            repeat: -1
        });
        this.anims.create({
            key: 'player-walk_side',
            frames: walkFrames.map(f => ({ key: 'kenneyChars', frame: f })),
            frameRate: 10,
            repeat: -1
        });

        // Keep legacy four-direction keys to avoid breaking other code
        this.anims.create({
            key: 'player-idle-down',
            frames: [{ key: 'kenneyChars', frame: stand }],
            frameRate: 1,
            repeat: -1
        });
        this.anims.create({
            key: 'player-idle-up',
            frames: [{ key: 'kenneyChars', frame: stand }],
            frameRate: 1,
            repeat: -1
        });
        this.anims.create({
            key: 'player-idle-left',
            frames: [{ key: 'kenneyChars', frame: stand }],
            frameRate: 1,
            repeat: -1
        });
        this.anims.create({
            key: 'player-idle-right',
            frames: [{ key: 'kenneyChars', frame: stand }],
            frameRate: 1,
            repeat: -1
        });

        this.anims.create({
            key: 'player-walk-down',
            frames: walkFrames.map(f => ({ key: 'kenneyChars', frame: f })),
            frameRate: 10,
            repeat: -1
        });
        this.anims.create({
            key: 'player-walk-up',
            frames: walkFrames.map(f => ({ key: 'kenneyChars', frame: f })),
            frameRate: 10,
            repeat: -1
        });
        this.anims.create({
            key: 'player-walk-left',
            frames: walkFrames.map(f => ({ key: 'kenneyChars', frame: f })),
            frameRate: 10,
            repeat: -1
        });
        this.anims.create({
            key: 'player-walk-right',
            frames: walkFrames.map(f => ({ key: 'kenneyChars', frame: f })),
            frameRate: 10,
            repeat: -1
        });

        this.scene.start('MainMenuScene'); // Transition to MainMenuScene after loading
    }
}
